import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Dropdown,
} from "react-bootstrap";
import CourseStructureService from "../../Services/CourseStructureService";
import toast from "react-hot-toast";

function AddCourseStructureModal({ show, onHide, programId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    description: "",
    subDescription: "",
    structure: {},
  });
  const [errors, setErrors] = useState({});
  const [years, setYears] = useState([1]);

  useEffect(() => {
    if (show) {
      setFormData({
        description: "",
        subDescription: "",
        structure: {},
      });
      setErrors({});
      setYears([1]);
    }
  }, [show]);

  const addYear = (selectedYear) => {
    if (years.length < 5 && !years.includes(selectedYear)) {
      const newYears = [...years, selectedYear].sort((a, b) => a - b);
      setYears(newYears);
    }
  };

  const removeYear = (yearToRemove) => {
    if (years.length > 1) {
      const newYears = years.filter((year) => year !== yearToRemove);
      setYears(newYears);

      // Remove from structure
      const newStructure = { ...formData.structure };
      delete newStructure[`year${yearToRemove}`];
      setFormData({ ...formData, structure: newStructure });
    }
  };

  const getAvailableYears = () => {
    return [1, 2, 3, 4, 5].filter((year) => !years.includes(year));
  };

  const updateYearData = (year, field, value) => {
    const yearKey = `year${year}`;
    const newStructure = {
      ...formData.structure,
      [yearKey]: {
        ...formData.structure[yearKey],
        [field]: value,
      },
    };
    setFormData({ ...formData, structure: newStructure });
  };

  const addCourse = (year) => {
    const yearKey = `year${year}`;
    const currentCourses = formData.structure[yearKey]?.courses || [];
    updateYearData(year, "courses", [...currentCourses, ""]);
  };

  const removeCourse = (year, courseIndex) => {
    const yearKey = `year${year}`;
    const currentCourses = formData.structure[yearKey]?.courses || [];
    const newCourses = currentCourses.filter(
      (_, index) => index !== courseIndex
    );
    updateYearData(year, "courses", newCourses);
  };

  const updateCourse = (year, courseIndex, value) => {
    const yearKey = `year${year}`;
    const currentCourses = formData.structure[yearKey]?.courses || [];
    const newCourses = [...currentCourses];
    newCourses[courseIndex] = value;
    updateYearData(year, "courses", newCourses);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.subDescription.trim()) {
      newErrors.subDescription = "Sub description is required";
    }

    // Validate structure
    if (Object.keys(formData.structure).length === 0) {
      newErrors.structure = "At least one year structure is required";
    }

    // Validate each year
    Object.entries(formData.structure).forEach(([yearKey, yearData]) => {
      if (!yearData.title?.trim()) {
        newErrors[`${yearKey}_title`] = "Year title is required";
      }
      if (!yearData.courses || yearData.courses.length === 0) {
        newErrors[`${yearKey}_courses`] = "At least one course is required";
      } else {
        yearData.courses.forEach((course, index) => {
          if (!course.trim()) {
            newErrors[`${yearKey}_course_${index}`] = "Course name is required";
          }
        });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    try {
      setLoading(true);
      const response = await CourseStructureService.addCourseStructure({
        description: formData.description,
        subDescription: formData.subDescription,
        programId,
        structure: formData.structure,
      });

      if (response.data.success) {
        toast.success("Course structure added successfully");
        onSuccess && onSuccess();
        onHide();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.log("Error adding course structure", error);
      toast.error("An error occurred while adding course structure");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "16px" }}>
          <i className="bi bi-plus-circle me-2"></i>
          Add Course Structure
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: "14px", fontWeight: "600" }}>
                  Description <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  isInvalid={!!errors.description}
                  style={{ fontSize: "14px" }}
                  placeholder="Enter course description..."
                />
                <Form.Control.Feedback
                  type="invalid"
                  style={{ fontSize: "12px" }}
                >
                  {errors.description}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: "14px", fontWeight: "600" }}>
                  Sub Description <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.subDescription}
                  onChange={(e) =>
                    setFormData({ ...formData, subDescription: e.target.value })
                  }
                  isInvalid={!!errors.subDescription}
                  style={{ fontSize: "14px" }}
                  placeholder="Enter sub description..."
                />
                <Form.Control.Feedback
                  type="invalid"
                  style={{ fontSize: "12px" }}
                >
                  {errors.subDescription}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 style={{ fontSize: "16px", fontWeight: "600" }}>
                <i className="bi bi-diagram-3 me-2"></i>
                Course Structure
              </h6>
              {years.length < 5 && getAvailableYears().length > 0 && (
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    size="sm"
                    style={{ fontSize: "14px" }}
                  >
                    <i className="bi bi-plus-circle me-1"></i>
                    Add Year
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {getAvailableYears().map((year) => (
                      <Dropdown.Item
                        key={year}
                        onClick={() => addYear(year)}
                        style={{ fontSize: "14px" }}
                      >
                        Year {year}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>

            {errors.structure && (
              <Alert variant="danger" style={{ fontSize: "12px" }}>
                {errors.structure}
              </Alert>
            )}

            <Row>
              {years.map((year, index) => (
                <Col md={6} key={year} className="mb-3">
                  <div className="card border">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <h6 className="mb-0" style={{ fontSize: "14px" }}>
                        Year {index + 1}
                      </h6>
                      {years.length > 1 && (
                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeYear(year)}
                          style={{ fontSize: "12px" }}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      )}
                    </div>
                    <div className="card-body">
                      <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: "14px" }}>
                          Year Title <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.structure[`year${year}`]?.title || ""}
                          onChange={(e) =>
                            updateYearData(year, "title", e.target.value)
                          }
                          isInvalid={!!errors[`year${year}_title`]}
                          style={{ fontSize: "14px" }}
                          placeholder="e.g., Foundation Year"
                        />
                        <Form.Control.Feedback
                          type="invalid"
                          style={{ fontSize: "12px" }}
                        >
                          {errors[`year${year}_title`]}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <div className="mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Label
                            style={{ fontSize: "14px", marginBottom: 0 }}
                          >
                            Courses <span className="text-danger">*</span>
                          </Form.Label>
                          <Button
                            type="button"
                            variant="outline-success"
                            size="sm"
                            onClick={() => addCourse(year)}
                            style={{ fontSize: "12px" }}
                          >
                            <i className="bi bi-plus me-1"></i>
                            Add Course
                          </Button>
                        </div>

                        {errors[`year${year}_courses`] && (
                          <Alert variant="danger" style={{ fontSize: "12px" }}>
                            {errors[`year${year}_courses`]}
                          </Alert>
                        )}

                        {(formData.structure[`year${year}`]?.courses || []).map(
                          (course, courseIndex) => (
                            <div
                              key={courseIndex}
                              className="d-flex align-items-center mb-2"
                            >
                              <Form.Control
                                type="text"
                                value={course}
                                onChange={(e) =>
                                  updateCourse(
                                    year,
                                    courseIndex,
                                    e.target.value
                                  )
                                }
                                isInvalid={
                                  !!errors[`year${year}_course_${courseIndex}`]
                                }
                                style={{ fontSize: "14px" }}
                                placeholder="Enter course name..."
                              />
                              <Button
                                type="button"
                                variant="outline-danger"
                                size="sm"
                                className="ms-2"
                                onClick={() => removeCourse(year, courseIndex)}
                                style={{ fontSize: "12px" }}
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                              {errors[`year${year}_course_${courseIndex}`] && (
                                <div
                                  className="invalid-feedback d-block"
                                  style={{ fontSize: "12px" }}
                                >
                                  {errors[`year${year}_course_${courseIndex}`]}
                                </div>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
            style={{ fontSize: "14px" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            style={{ fontSize: "14px" }}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Adding...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Add Course Structure
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default AddCourseStructureModal;
